'use client';

import { useEffect, useState } from 'react';
import {
    Button,
    Modal,
    TextInput,
    Textarea,
    Divider,
    Checkbox,
    Stack,
    Switch
} from '@mantine/core';
import { Dropzone } from '../EditorText/Dropzone';
import { LinkFieldInput } from './LinkFieldInput';
import { parseJwt } from '@/src/helpers/decode-jwt';
import Cookies from 'js-cookie';
import { AddSliceModal } from './AddSlice/AddSliceModal';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';

type SliceField = {
    key: string;
    label: string;
    value: string;
};

type EditableSliceWrapperProps = {
    children: React.ReactNode;
    slice: Record<string, any>;
    sliceIndex: number;
    lang: 'fr' | 'en';
    pageKey: string;
};

export const EditableSliceWrapper = ({
    children,
    slice,
    sliceIndex,
    lang,
    pageKey,
}: EditableSliceWrapperProps) => {
    const [opened, setOpened] = useState(false);
    const [addModalOpened, setAddModalOpened] = useState(false);
    const [formValues, setFormValues] = useState<Record<string, any>>(structuredClone(slice));
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const decoded = parseJwt(token);
                if (decoded?.role === 'superAdmin') {
                    setIsSuperAdmin(true);
                }
            } catch (err) {
                console.error('Invalid token', err);
            }
        }
    }, []);

    const editableFields = flattenFields(slice);
    const isEditableType = slice.type !== 'space' && slice.type !== 'divider';

    const handleSave = async () => {
        const response = await fetch('/api/slices', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                lang,
                pageKey,
                sliceIndex,
                updates: formValues,
            }),
        });

        if (response.ok) {
            addDeploymentToast('Déploiement des slices en cours. Optimisation des fichiers...');
            setOpened(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Supprimer ce bloc ?')) return;
        const res = await fetch('/api/slices/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pageKey,
                sliceIndex,
                locale: lang,
            }),
        });

        if (res.ok) {
            addDeploymentToast('Suppression des slices en cours. Optimisation des fichiers...');
            setOpened(false);
        } else {
            alert('Erreur lors de la suppression');
        }
    };

    const handleAdd = async (slice: any) => {
        const res = await fetch('/api/slices/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pageKey,
                newSlice: slice,
                locale: lang,
                position: sliceIndex + 1,
            }),
        });

        if (res.ok) {
            addDeploymentToast('Déploiement des nouvelles slices en cours. Optimisation des fichiers...');
            setAddModalOpened(false);
        } else {
            alert('Erreur lors de l’ajout du bloc');
        }
    };

    const handleChange = (path: string, value: string) => {
        const updated = structuredClone(formValues);
        setDeepValue(updated, path, value);
        setFormValues(updated);
    };

    return (
        <div style={{ border: '1px dashed #ccc', marginBottom: '2rem' }}>
            {isEditableType && isSuperAdmin && editableFields.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem', gap: '0.5rem', marginBottom: 10 }}>
                    <Button size="xs" color="black" onClick={() => setOpened(true)}>
                        Modifier
                    </Button>
                    <Button size="xs" color="red" onClick={handleDelete}>
                        Supprimer
                    </Button>
                </div>
            )}

            {children}

            {isSuperAdmin && (
                <>
                    <div
                        onClick={() => setAddModalOpened(true)}
                        style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                            padding: '1rem 0',
                            color: '#aaa',
                            userSelect: 'none'
                        }}
                    >
                        <Divider label="+ Choisir l'élement à ajouter" labelPosition="center" />
                    </div>

                    <AddSliceModal
                        opened={addModalOpened}
                        onClose={() => setAddModalOpened(false)}
                        onAdd={handleAdd}
                    />
                </>
            )}

            <Modal opened={opened} onClose={() => setOpened(false)} title="Modifier ce bloc" size="lg" centered>
                {editableFields.map(({ key, label }) => {
                    const value = getDeepValue(formValues, key);
                    const isImage = key.toLowerCase().includes('image') || key === 'src';
                    const isLink = /link|href/i.test(key);
                    const isDescription = key.toLowerCase().includes('description');

                    return (
                        <div key={key} style={{ marginBottom: 20 }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>{label}</label>
                            {isImage ? (
                                // @ts-ignore
                                <Dropzone imageUrl={value} setImageUrl={(url: string) => handleChange(key, url)} />
                            ) : isLink ? (
                                <LinkFieldInput value={value} onChange={(val) => handleChange(key, val)} lang={lang} />
                            ) : isDescription ? (
                                <Textarea value={value} onChange={(e) => handleChange(key, e.currentTarget.value)} autosize minRows={3} />
                            ) : key === 'version' ? (
                                <Switch
                                    label="Thème sombre"
                                    checked={value === 'dark'}
                                    onChange={(e) => handleChange(key, e.currentTarget.checked ? 'dark' : 'light')}
                                />
                            ) : typeof value === 'boolean' ? (
                                <Checkbox
                                    label={label}
                                    checked={value}
                                    // @ts-ignore
                                    onChange={(e) => handleChange(key, e.currentTarget.checked)}
                                />
                            ) : (
                                <TextInput value={value} onChange={(e) => handleChange(key, e.currentTarget.value)} />
                            )}
                        </div>
                    );
                })}

                <Button fullWidth mt="md" onClick={handleSave}>
                    Enregistrer
                </Button>
            </Modal>
        </div>
    );
};

// Helpers

function flattenFields(obj: any, prefix = ''): SliceField[] {
    const fields: SliceField[] = [];
    Object.entries(obj).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'string' || typeof value === 'boolean') {
            fields.push({ key: path, label: key, value: value.toString() });
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => fields.push(...flattenFields(item, `${path}[${index}]`)));
        } else if (typeof value === 'object' && value !== null) {
            fields.push(...flattenFields(value, path));
        }
    });
    return fields;
}

function setDeepValue(obj: any, path: string, value: any): void {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in current)) current[k] = {};
        current = current[k];
    }
    current[keys[keys.length - 1]] = value;
}

function getDeepValue(obj: any, path: string): any {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    return keys.reduce((acc, key) => acc?.[key], obj);
}
